<template>
  <div class="d-flex justify-center align-center h-100">
    <v-card class="pa-6" elevation="3" max-width="400">
      <v-card-text class="text-center">
        <v-progress-circular 
          indeterminate 
          color="primary" 
          size="64"
          class="mb-4"
        ></v-progress-circular>
        <h3 class="mb-2">{{ statusMessage }}</h3>
        <p class="text-body-2">{{ detailMessage }}</p>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import authService from '@/services/auth';
import { useAuthStore } from '@/stores/auth';
import { useSystemStore } from '@/stores/system';

export default {
  name: 'Callback',
  data() {
    return {
      statusMessage: 'Processing login...',
      detailMessage: 'Please wait while we complete your authentication.'
    };
  },
  async created() {
    console.log('DEBUG: Callback component created');
    console.log('DEBUG: Current URL:', window.location.href);
    console.log('DEBUG: Router current route:', this.$route);
    await this.handleCallback();
  },
  methods: {
    async handleCallback() {
      try {
        // Debug: Log all session storage items
        console.log('CALLBACK - Session storage items:', {
          fromOktaCallback: sessionStorage.getItem('fromOktaCallback'),
          oktaAuthCode: sessionStorage.getItem('oktaAuthCode') ? 'present' : 'missing',
          oktaState: sessionStorage.getItem('oktaState') ? 'present' : 'missing',
          oktaError: sessionStorage.getItem('oktaError'),
          oktaCallbackUrl: sessionStorage.getItem('oktaCallbackUrl')
        });

        // Check if we came from Okta callback
        const fromOktaCallback = sessionStorage.getItem('fromOktaCallback');
        const oktaAuthCode = sessionStorage.getItem('oktaAuthCode');
        const oktaState = sessionStorage.getItem('oktaState');
        const oktaError = sessionStorage.getItem('oktaError');
        const oktaErrorDescription = sessionStorage.getItem('oktaErrorDescription');

        if (!fromOktaCallback) {
          throw new Error('Invalid callback - not from Okta');
        }

        if (oktaError) {
          const errorMsg = oktaErrorDescription ? `${oktaError}: ${oktaErrorDescription}` : oktaError;
          throw new Error(`Okta authentication error: ${errorMsg}`);
        }

        if (!oktaAuthCode) {
          console.error('No authorization code found. Full callback URL was:', sessionStorage.getItem('oktaCallbackUrl'));
          throw new Error('No authorization code received from Okta. Please check the callback URL configuration.');
        }

        this.statusMessage = 'Processing Okta authentication...';
        console.log('DEBUG: Starting token exchange with auth code...');
        console.log('DEBUG: Auth code present:', !!oktaAuthCode);
        console.log('DEBUG: State present:', !!oktaState);
        
        // Build the current URL that Okta expects for token exchange
        const callbackUrl = sessionStorage.getItem('oktaCallbackUrl') || `${window.location.origin}/auth/callback?code=${oktaAuthCode}&state=${oktaState || ''}`;
        
        // Use parseFromUrl method which handles the token exchange internally
        try {
          const tokens = await authService.oktaAuth.token.parseFromUrl(callbackUrl);
          console.log('DEBUG: Token exchange successful:', {
            accessToken: tokens.tokens.accessToken ? 'present' : 'missing',
            idToken: tokens.tokens.idToken ? 'present' : 'missing'
          });
          
          // Store the tokens
          authService.oktaAuth.tokenManager.setTokens(tokens.tokens);
        } catch (parseError) {
          console.error('Token parsing failed, trying direct exchange...', parseError);
          
          // Fallback: try direct token exchange
          const tokenParams = { code: oktaAuthCode };
          if (oktaState) {
            tokenParams.state = oktaState;
          }
          
          console.log('Attempting direct token exchange with params:', tokenParams);
          await authService.oktaAuth.token.exchangeCodeForTokens(tokenParams);
        }

        // Clear the callback markers after successful token exchange
        sessionStorage.removeItem('fromOktaCallback');
        sessionStorage.removeItem('oktaAuthCode');
        sessionStorage.removeItem('oktaState');
        sessionStorage.removeItem('oktaError');
        sessionStorage.removeItem('oktaErrorDescription');
        sessionStorage.removeItem('oktaCallbackUrl');

        this.statusMessage = 'Getting user information...';
        
        // Get user info from Okta
        const userInfo = await authService.getUserInfo();
        const oktaToken = await authService.getAccessToken();
        
        console.log('User info retrieved:', {
          email: userInfo?.email,
          name: userInfo?.name,
          tokenPresent: oktaToken ? 'yes' : 'no'
        });
        
        if (!userInfo || !userInfo.email) {
          throw new Error('Unable to retrieve user information from Okta');
        }

        this.statusMessage = 'Exchanging tokens...';
        this.detailMessage = 'Converting Okta authentication to RISE access...';
        
        // Exchange Okta token for RISE token
        await this.exchangeOktaTokenForRiseToken(userInfo.email, oktaToken);
        
      } catch (error) {
        console.error('Callback error:', error);
        console.error('Error stack:', error.stack);
        this.statusMessage = 'Authentication Error';
        this.detailMessage = error.message || 'An error occurred during authentication.';
        
        // Redirect to login after a delay
        setTimeout(() => {
          this.$router.push({ name: 'login' });
        }, 5000);
      }
    },

    async exchangeOktaTokenForRiseToken(email, oktaToken) {
      const authStore = useAuthStore();
      const systemStore = useSystemStore();

      try {
        // Call the backend API to exchange Okta token for RISE token
        const response = await authStore.ssoLogin(email, oktaToken);
        
        if (response.success) {
          this.statusMessage = 'Login successful!';
          this.detailMessage = 'Redirecting to your dashboard...';
          
          // Set user as authorized
          authStore.userAuthorized = true;
          
          // Redirect to the URL they requested (persisted before SSO redirect; survives full page load)
          const intendedPath = sessionStorage.getItem('ssoIntendedPath');
          const originalUri = authService.getOriginalUri();
          const redirectPath = (intendedPath && intendedPath !== '/' && intendedPath !== 'login')
            ? intendedPath
            : (originalUri && originalUri !== '/' && originalUri !== 'login' ? originalUri : null);
          
          // Default home by user type if no intended path
          let redirectRoute = 'home-partner';
          if (systemStore.userDetail?.userType) {
            const userType = systemStore.userDetail.userType.trim();
            if (userType === 'Distributor') {
              redirectRoute = 'home-distributor';
            } else if (['Partner', 'Dell', 'TP', 'PAM', 'Dell Marketing', 'TIC_Admin'].includes(userType)) {
              redirectRoute = 'home-partner';
            }
          }
          
          console.log(`DEBUG: SSO Login successful for ${email}. Redirect path:`, redirectPath || redirectRoute);
          
          // Clear one-time redirect target so next login is not forced there
          sessionStorage.removeItem('ssoIntendedPath');
          systemStore.requestedRoute = '';
          
          // Navigate to the requested URL or default home
          setTimeout(() => {
            if (redirectPath) {
              console.log('DEBUG: Redirecting to requested URL:', `/${redirectPath}`);
              this.$router.push(`/${redirectPath}`);
            } else {
              console.log('DEBUG: Redirecting to default home:', redirectRoute);
              this.$router.push({ name: redirectRoute });
            }
          }, 1000);
          
        } else {
          throw new Error(response.message || 'Failed to exchange Okta token for RISE access');
        }
        
      } catch (error) {
        console.error('Token exchange error:', error);
        throw new Error('Failed to complete authentication. Please try again.');
      }
    }
  }
};
</script>

<style scoped>
.h-100 {
  height: 100vh;
}
</style>